import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Chip,
  Divider,
  useTheme,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { useAccountBookStore } from '@/store/accountBookStore';
import { signOutUser } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

const AccountSwitcher: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const { 
    currentAccountNumber, 
    currentAccountBook,
    accountBooks,
    setCurrentAccountBook,
    userId
  } = useAccountBookStore();
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);


  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccountSelect = async (accountNumber: number) => {
    setLoading(true);
    try {
      setCurrentAccountBook(accountNumber);
      // 가계부 전환 후 잠시 대기 (데이터 로딩 완료를 위해)
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error('❌ AccountSwitcher: 가계부 전환 실패:', error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await signOutUser();
    } catch (error) {
      console.error('❌ AccountSwitcher: 로그아웃 실패:', error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };


  // 설정 액션 제거 (요구사항에 따라 프로필은 사용자 ID와 로그아웃만 표시)

  // 기본 5개 매장 슬롯(1-5번) 생성
  const displayAccountBooks = React.useMemo(() => {
    // accountBooks를 accountNumber로 매핑
    const accountBookMap = new Map<number, typeof accountBooks[0]>();
    accountBooks.forEach(ab => {
      if (ab.accountNumber >= 1 && ab.accountNumber <= 5) {
        accountBookMap.set(ab.accountNumber, ab);
      }
    });

    // 1-5번 슬롯 생성 (없으면 빈 슬롯)
    const slots: Array<typeof accountBooks[0] | { accountNumber: number; isEmpty: true }> = [];
    for (let accountNumber = 1; accountNumber <= 5; accountNumber++) {
      const existingBook = accountBookMap.get(accountNumber);
      if (existingBook) {
        slots.push(existingBook);
      } else {
        slots.push({ accountNumber, isEmpty: true } as any);
      }
    }
    return slots;
  }, [accountBooks]);

  // 현재 매장 정보
  const currentAccountBookInfo = accountBooks.find(ab => ab.accountNumber === currentAccountNumber);

  return (
    <Box>
      <Tooltip title="매장 전환">
        <Button
          onClick={handleClick}
          variant="outlined"
          endIcon={<span style={{ fontSize: 16, lineHeight: 1 }}>{GLYPHS.down}</span>}
          disabled={loading}
          sx={{
            minWidth: 200,
            justifyContent: 'space-between',
            borderColor: 'divider',
            color: 'text.primary',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Box sx={{ textAlign: 'left', flexGrow: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {currentAccountBookInfo 
                ? `${currentAccountBookInfo.accountNumber}번 가게 - ${currentAccountBookInfo.businessName || currentAccountBookInfo.name}`
                : currentAccountBook?.name || '매장 선택'}
            </Typography>
          </Box>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 280,
            maxHeight: 400,
            boxShadow: theme.shadows[8],
            borderRadius: 2,
          },
        }}
      >
        {/* 헤더 */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            매장 선택
          </Typography>
          <Typography variant="caption" color="text.secondary">
            총 5개 슬롯 ({accountBooks.filter(ab => ab.accountNumber >= 1 && ab.accountNumber <= 5).length}개 사용 중)
          </Typography>
        </Box>


        {/* 로딩 표시 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* 매장 목록 (항상 5개 슬롯 표시) */}
        {displayAccountBooks.map((slot) => {
          const isEmpty = 'isEmpty' in slot;
          const accountBook = isEmpty ? null : slot;
          const accountNumber = isEmpty ? slot.accountNumber : accountBook.accountNumber;
          const isCurrent = accountNumber === currentAccountNumber;

          return (
            <MenuItem
              key={isEmpty ? `empty-${accountNumber}` : accountBook.id}
              onClick={() => handleAccountSelect(accountNumber)}
              selected={isCurrent}
              sx={{
                py: 1.5,
                px: 2,
                opacity: isEmpty ? 0.7 : 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {accountNumber}번 가게
                  </Typography>
                  {isEmpty && (
                    <Chip
                      label="정보 없음"
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                  {isCurrent && (
                    <Chip
                      label="현재"
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                {isEmpty ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    매장 정보 미등록 (데이터는 조회 가능)
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {accountBook.businessName || accountBook.name}
                    </Typography>
                    {accountBook.description && (
                      <Typography variant="caption" color="text.secondary">
                        {accountBook.description}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </MenuItem>
          );
        })}

        <Divider />

        {/* 프로필 정보: 사용자 이메일 & 로그아웃 */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            로그인 이메일
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)', mb: 1 }}>
            {user?.email || '-'}
          </Typography>
          <Button
            fullWidth
            color="error"
            variant="outlined"
            onClick={handleLogout}
            disabled={loading}
            size="small"
          >
            로그아웃
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default AccountSwitcher;
